import { useState, useEffect } from 'react';
import adminApi from '@/utils/adminApi';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Settings, ImageIcon, DollarSign } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSettings() {
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [priceVisible, setPriceVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.get(`/settings`);
      setWatermarkEnabled(response.data.watermark_enabled);
      setPriceVisible(response.data.price_visible !== false);
    } catch (error) {
      // Default to enabled
      setWatermarkEnabled(true);
      setPriceVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWatermark = async (enabled) => {
    setSaving(true);
    try {
      await adminApi.put(`/settings`, { watermark_enabled: enabled, price_visible: priceVisible });
      setWatermarkEnabled(enabled);
      toast.success(`Watermark ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePrice = async (visible) => {
    setSaving(true);
    try {
      await adminApi.put(`/settings`, { watermark_enabled: watermarkEnabled, price_visible: visible });
      setPriceVisible(visible);
      toast.success(`Prices ${visible ? 'visible' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-stone-100">
        <p className="text-stone-500">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-stone-100">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="w-6 h-6 text-[#E85D75]" />
        <h2 className="text-2xl font-serif font-medium text-stone-800">Site Settings</h2>
      </div>

      <div className="space-y-4">
        {/* Watermark Toggle */}
        <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E85D75]/10 rounded-full flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-[#E85D75]" />
            </div>
            <div>
              <p className="font-medium text-stone-800">Image Watermark</p>
              <p className="text-sm text-stone-500">Show "kittens4sale.us" on all images</p>
            </div>
          </div>
          <Switch
            checked={watermarkEnabled}
            onCheckedChange={handleToggleWatermark}
            disabled={saving}
            data-testid="watermark-toggle"
          />
        </div>

        {/* Price Visibility Toggle */}
        <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E85D75]/10 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#E85D75]" />
            </div>
            <div>
              <p className="font-medium text-stone-800">Show Prices</p>
              <p className="text-sm text-stone-500">Display kitten prices on the website</p>
            </div>
          </div>
          <Switch
            checked={priceVisible}
            onCheckedChange={handleTogglePrice}
            disabled={saving}
            data-testid="price-toggle"
          />
        </div>

        {/* Status indicators */}
        <div className="text-sm text-stone-500 pl-4 space-y-1">
          <p>Watermark: <span className={watermarkEnabled ? 'text-green-600' : 'text-stone-400'}>
            {watermarkEnabled ? 'Visible on all images' : 'Hidden'}
          </span></p>
          <p>Prices: <span className={priceVisible ? 'text-green-600' : 'text-stone-400'}>
            {priceVisible ? 'Visible to visitors' : 'Hidden from visitors'}
          </span></p>
        </div>
      </div>
    </div>
  );
}
